/**
 * useSeeAlso Hook
 *
 * Queries for resources of the same rdf:type as the current entity.
 * Uses the authenticated SPARQL endpoint; degrades gracefully if
 * the user is not logged in or the query fails.
 */

import { useState, useEffect } from 'react';
import sparqlService from '@services/sparql.service';
import type { EntityProperty } from '@/types';

const RDF_TYPE = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type';
const MAX_RESULTS = 10;

export interface SeeAlsoResult {
  resources: string[];
  typeUri: string | null;
  loading: boolean;
}

/**
 * Returns up to MAX_RESULTS resources that share the same primary rdf:type.
 */
export function useSeeAlso(uri: string, properties: EntityProperty[], enabled = true): SeeAlsoResult {
  const [resources, setResources] = useState<string[]>([]);
  const [typeUri, setTypeUri] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const typeProp = properties.find((p) => p.predicate === RDF_TYPE);
    const typeObj = typeProp?.objects.find((o) => o.type === 'uri');

    if (!typeObj || !uri || !enabled) {
      setResources([]);
      setTypeUri(null);
      return;
    }

    const type = typeObj.value;
    let cancelled = false;
    setLoading(true);

    const query = `
SELECT DISTINCT ?resource WHERE {
  ?resource a <${type}> .
  FILTER(?resource != <${uri}>)
}
LIMIT ${MAX_RESULTS}
    `.trim();

    sparqlService
      .query(query)
      .then((bindings) => {
        if (cancelled) return;
        setTypeUri(type);
        setResources(bindings.map((b) => b.resource?.value).filter(Boolean) as string[]);
      })
      .catch(() => {
        // Silently ignore auth errors – the section just stays hidden
        if (!cancelled) setResources([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uri, properties, enabled]);

  return { resources, typeUri, loading };
}
